if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('space-armory-cart') || document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('space-armory-cart, cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        const purchase = this.closest('product-info')?.faceFitPurchase;
        if (purchase) {
          if (!purchase.size) purchase.openScanner();
          else purchase.addToCart(evt).catch(() => {});
          return;
        }
        if (this.submitButton.getAttribute('aria-disabled') === 'true' || this.submitButton.disabled) return;
        this.addToCart(evt).catch(() => {});
      }

      addToCart(evt, {beforeCartRender} = {}) {
        if (this.pendingAdd) return this.pendingAdd;
        this.handleErrorMessage();
        this.error = false;
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        const formData = new FormData(this.form);
        window.SpaceArmoryFitDevice?.decorateForm(formData);
        window.SpaceArmorySizingAttribution?.decorateForm(formData, this.form);
        if (this.cart) {
          formData.set('sections', this.cart.getSectionsToRender().map(section => section.id));
          formData.set('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);
        let prepared = false;
        const prepareCartRender = () => { if (!prepared) { prepared = true; beforeCartRender?.(); } };
        this.pendingAdd = Promise.resolve().then(async () => {
          if (!variantId) throw new Error('Choose an available size before adding to your cart.');
          const nativeCart = this.cart?.addForm;
          const http = nativeCart ? null : await fetch(routes.cart_add_url, config);
          const response = nativeCart ? await this.cart.addForm(formData, {onAdded: () => {
            if (this.closest('quick-add-modal')) return;
            prepareCartRender();
            this.cart.open?.(this.cart.activeElement, false);
          }}) : await http.json();
          if ((http && !http.ok) || response.status) {
            const error = new Error(response.description || response.message || 'Could not add to your cart. Please try again.');
            error.cartStatus = response.status || http?.status;
            throw error;
          }
          this.resolveCartLinesUpdate(linesUpdateDeferred, nativeCart ? this.cart.store?.cart : undefined);
          // A successful POST is final. Rendering/analytics failures must not invite a duplicate retry.
          try {
            prepareCartRender();
            if (!this.cart) {
              window.location.assign(window.routes.cart_url);
              return response;
            }
            if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            const marker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form', productVariantId: variantId, cartData: response,
            })).then(() => CartPerformance.measureFromMarker('add:wait-for-subscribers', marker)).catch(console.error);
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener('modalClosed', () => setTimeout(() => this.cart.renderContents(response)), {once: true});
              quickAddModal.hide(true);
            } else if (!nativeCart) {
              // The Space Armory drawer already opens after the successful POST.
              // Opening it again after the cart refresh can undo a customer's close.
              CartPerformance.measure('add:paint-updated-sections', () => this.cart.renderContents(response));
            }
          } catch (renderError) {
            console.error(renderError);
            window.location.assign(window.routes.cart_url);
          }
          return response;
        }).catch(error => {
          this.error = true;
          const message = error.message || 'Could not add to your cart. Please try again.';
          this.handleErrorMessage(message);
          publish(PUB_SUB_EVENTS.cartError, {source: 'product-form', productVariantId: variantId, errors: message, message});
          this.dispatchCartErrorEvent(message, error.cartStatus ? 'INVALID' : 'SERVICE_UNAVAILABLE');
          linesUpdateDeferred?.reject(error);
          throw error;
        }).finally(() => {
          this.pendingAdd = null;
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner')?.classList.add('hidden');
          if (evt) CartPerformance.measureFromEvent('add:user-action', evt);
        });
        return this.pendingAdd;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred, confirmedCart) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = confirmedCart?.currency ? Promise.resolve(confirmedCart) : typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
